import  * as ModelerActions from "./ModelerActions.js";
import * as ModelerQueries from "../../../graphql/queries/modeler-queries";
import {
  graphQLClient,
  executeGraphQLRequest,
  createContext,
} from "../../../graphql/graphql-client";
import { ACCESS_TYPES } from "../../constants/AppEnums.js";
import { CreateModelerFromTemplate } from "../../models/ModelerModel.js";
import { removeS3Folder } from "../../helpers/S3Helpers.js";
import { API } from "aws-amplify";
import {
  getSignedUrl,
  removeS3File,
  uploadFileToS3,
} from "../../helpers/S3Helpers.js";
import { GET_SIGNED_URL_PATH } from "../../constants/ApiConstants.js";
import { RESOURCE_TYPES } from "../../constants/AppEnums.js";

export const fetchModelers = (filters, from, size) => {
  return (dispatch, getState) => {
    const state = getState();
    const user_id = state.user.userId;
    executeGraphQLRequest(
      dispatch,
      getState,
      {
        begin: ModelerActions.fetchModelersBegin, // Initial dispatch
        success: ModelerActions.fetchModelersSuccess, // Success dispatch
        failure: ModelerActions.fetchModelersFailure // Failure dispatch
      },
      ModelerQueries.GET_MODELERS_BY_FILTER_QUERY,
      {
        filter: filters,
        from: from,
        size: size,        
        context: createContext(user_id, "LIST", "MODELER"), // Assuming you have a similar context creation function
      }
    );
  };
};

export const fetchModelerById = (modelerId) => {
  return (dispatch, getState) => {
    const user_id = getState().user.userId;
    executeGraphQLRequest(
      dispatch,
      getState,
      {
        begin: ModelerActions.fetchModelerByIdBegin,  // Initial dispatch
        success: ModelerActions.fetchModelerByIdSuccess,  // Success dispatch
        failure: ModelerActions.fetchModelerByIdFailure  // Failure dispatch
      },
      ModelerQueries.GET_MODELER_BY_ID_QUERY,
      {
        id: {primaryId: modelerId},
        context: createContext(user_id, "READ", "MODELER", modelerId)  // Assuming you have a similar context creation function
      }
    );
  };
};

export const cloneModeler = ({ user, modelerId, modelerTitle, access }) => {
  return (dispatch, getState) => {
    const user_id = getState().user.userId;
    executeGraphQLRequest(
      dispatch,
      getState,
      {
        begin: ModelerActions.cloneModelerBegin,  // Initial dispatch
        success: ModelerActions.cloneModelerSuccess,  // Success dispatch
        failure: ModelerActions.cloneModelerFailure  // Failure dispatch
      },
      ModelerQueries.CLONE_MODELER_MUTATION,
      {
        input: {
          id: modelerId,
          title: modelerTitle,
          access: access,
        },
        context: createContext(user_id, "CLONE", "MODELER", modelerId)  // Assuming you have a similar context creation function
      }
    );
  };
};



export const deleteModeler = (modelerId) => {
  return (dispatch, getState) => {
    const user_id = getState().user.userId;
    executeGraphQLRequest(
      dispatch,
      getState,
      {
        begin: ModelerActions.deleteModelerBegin,  // Initial dispatch
        success: ModelerActions.deleteModelerSuccess,  // Success dispatch
        failure: ModelerActions.deleteModelerFailure  // Failure dispatch
      },
      ModelerQueries.DELETE_MODELER_MUTATION,
      {
        input: { primaryId: modelerId },
        context: createContext(user_id, "DELETE", "MODELER", modelerId)  // Assuming you have a similar context creation function
      }
    );
  };
}

export const updateModeler = (modelerId, modeler) => {
  return (dispatch, getState) => {
    const user_id = getState().user.userId;
    const { title, description, notes, report, ...updateModeler} = modeler;
    executeGraphQLRequest(
      dispatch,
      getState,
      {
        begin: ModelerActions.updateModelerBegin,  // Initial dispatch
        success: ModelerActions.updateModelerSuccess,  // Success dispatch
        failure: ModelerActions.updateModelerFailure  // Failure dispatch
      },
      ModelerQueries.UPDATE_MODELER_MUTATION,
      {
        input: {
          title: title,
          description: description,
          notes: notes,
          report: report,
        },
        condition: { primaryId: modelerId },
        context: createContext(user_id, "UPDATE", "MODELER", modelerId)  // Assuming you have a similar context creation function
      }
    );
  };
}

      


export const uploadModelerThumbnailImage = (imageFile) => {
  console.log("Uploading modeler thumbnail image...");
  return async (dispatch, getState) => {
    const state = getState();
    const { id, thumbnailImage: imageName } =
      state.modeler.activeModeler;
    const resourcePath = `${id}/${imageName}`;

    try {
      await uploadFileToS3(RESOURCE_TYPES.MODELER, resourcePath, imageFile);
      console.log("File uploaded successfully");

      try {
        const s3url = await getSignedUrl(
          GET_SIGNED_URL_PATH,
          RESOURCE_TYPES.TEMPLATE,
          resourcePath
        );
        dispatch(ModelerActions.updateModelerThumbnailImage({ imageUrl: s3url }));
      } catch (error) {
        console.log("Error getting signed URL:", error);
      }
    } catch (error) {
      console.error("Error uploading file:", error);
    }
  };
};

export const executeReportPipeline = (modelerId, modelRuns) => {
  return (dispatch, getState) => {
    const user_id = getState().user.userId;
    
    // Validate inputs
    if (!modelerId) {
      console.error("Modeler ID is required");
      return Promise.reject(new Error("Modeler ID is required"));
    }
    
    if (!modelRuns || modelRuns.length === 0) {
      console.error("At least one model run is required");
      return Promise.reject(new Error("At least one model run is required"));
    }
    
    // Prepare the request structure according to the new API schema
    const requestInput = {
      modelerId: modelerId,
      modelRuns: modelRuns.map(run => {
        // Extract asset IDs, handling both object and string formats
        const vtszAssetIds = (run.selectedVtszAssets || []).map(asset => 
          typeof asset === 'object' ? asset.id : asset
        ).filter(id => id); // Filter out undefined/null values
        
        const mediaAssetIds = (run.selectedMediaAssets || []).map(asset => 
          typeof asset === 'object' ? asset.id : asset
        ).filter(id => id); // Filter out undefined/null values
        
        const allAssetIds = [...vtszAssetIds, ...mediaAssetIds];
        
        // Log the asset extraction for debugging
        console.log(`Model Run ${run.id}:`, {
          vtszAssets: run.selectedVtszAssets,
          mediaAssets: run.selectedMediaAssets,
          extractedAssetIds: allAssetIds
        });
        
        return {
          id: run.id,
          assetIds: allAssetIds
        };
      })
    };

    // Create context according to the API schema
    const context = {
      requesterId: user_id,
      requestType: "CREATE",
      resourceType: "MODELER",
      entityType: "REPORT",
      resourceId: modelerId
    };

    console.log("Executing report pipeline with input:", requestInput);
    console.log("Context:", context);

    // Dispatch the begin action
    dispatch(ModelerActions.publishModelerReportBegin());

    return graphQLClient()
      .then(client => client.request(
        ModelerQueries.EXECUTE_REPORT_PIPELINE_MUTATION, 
        { 
          input: requestInput,
          context: context
        }
      ))
             .then(res => {
         // Extract the generatedReportId from the response
         const generatedReportId = res?.executeReportPipeline?.generatedReportId;
         if (generatedReportId) {
           console.log("Report generated successfully. Report ID:", generatedReportId);
           // Store the generatedReportId in the state
           dispatch(ModelerActions.publishModelerReportSuccess( generatedReportId ));
         } else {
           console.error("No generatedReportId received from the API");
           dispatch(ModelerActions.publishModelerReportFailure("No report ID received"));
         }
       })
      .catch(err => {
        console.error("GraphQL request failed:", err);
        dispatch(ModelerActions.publishModelerReportFailure(err.message || "Request failed"));
      });
  };
};

// export const createUpdateModeler = (template) => {
//   return (dispatch, getState ) => {
//     let modeler = CreateModelerFromTemplate(template);
//     let state = getState();
//     let requester_id = state.user.userId;
//     dispatch(createUpdateModelerBegin());
//     graphQLClient()
//       .request(ModelerQueries.CREATE_NEW_MODELER_MUTATION, {
//         modeler: modeler,
//         requester_id: requester_id
//       })
//       .then((res) => {
//         dispatch(togglePublishTemplate(true, res.createModeler.id));
//         postData(res.createModeler).then((res) => {
//           console.log(JSON.stringify(res));
//         });
//       })
//       .then((res) => {
//         dispatch(createUpdateModelerSuccess(res));
//       })
//       .catch((err) => {});
//   };
// };

// export const deleteModeler = (modelerId) => {
//   return (dispatch, getState) => {
//     dispatch(deleteModelerBegin());
//     let state = getState();
//     graphQLClient()
//       .request(ModelerQueries.DELETE_MODELER_MUTATION, {
//         input: { id: modelerId },
//       })
//       .then((res) => {
//         dispatch(togglePublishTemplate(false, null));
//         removeS3Folder(state.template.activeTemplate.modelerAccess, `${modelerId}`).then((res) => {
//           console.log(`removed ${res}`);
//         });
//       })
//       .then((res) => {
//         dispatch(deleteModelerSuccess(res));
//       })
//       .catch((err) => {});
//   };
// };
